"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { COPYRIGHT_YEAR } from "@/lib/constants";
import CorporateMark from "public/svgs/wrapDotGiftsCorporateMark.svg";

const ROUTE_PREFIXES_TO_SHOW_FOOTER = [
  "/myAccount",
  "/myCards",
  "/myTemplates",
  "/privacy",
  "/stripe",
  "/tos",
  "/about",
  "/cookies",
  "/policies",
  "/fathersday",
  "/artistEditor",
  "/editor",
];

/**
 * The red of the bow on the corporate mark — `.cls-5` in
 * wrapDotGiftsCorporateMark.svg, which is what every topmost path of the bow
 * is filled with. Hardcoded because this repo has no brand tokens in the
 * Tailwind config yet; when `packages/brand/` lands (about-site-lld.md §5.2)
 * this should become a token rather than a literal in two places.
 */
const BOW_RED = "#ec4646";

/**
 * The blue-purple of the box body — `.cls-3` in the same file. Pairing it
 * with the red reproduces the mark's own contrast: in the artwork the red
 * ribbon crosses exactly this blue, so the divider is a miniature of the
 * logo rather than a dot that merely happens to be brand-coloured.
 */
const BOX_BLUE = "#8dacfb";

/**
 * Divider between footer items — a drawn circle in the mark's two colours.
 *
 * The row is a long single line, a corporate statement plus four links, and
 * the gap alone left "© 2026 About Us" reading as one phrase.
 *
 * Blue-purple core, red ring — the same way round as the artwork, where the
 * red ribbon sits ON the blue box rather than under it. 4px of blue inside a
 * 1px red ring, 6px overall.
 *
 * 1px is the thinnest ring that renders reliably: a sub-pixel spread lands
 * crisply on a 2x display but antialiases to a washed-out smear at 1x, which
 * is worse than no ring at all.
 *
 * Drawn rather than typed: a `·` glyph is a font artefact whose size, weight
 * and vertical position change with the typeface, and at this size it reads
 * as grit rather than as a deliberate mark. A `rounded-full` span is exactly
 * round, exactly 4px, and exactly the mark's colours on every face.
 *
 * The ring is a box-shadow rather than a border: a border is drawn INSIDE the
 * box, so `border` on a 4px dot would leave 2px of core, whereas a shadow is
 * drawn outside it and the core keeps its full size.
 *
 * Both colours are inline rather than Tailwind arbitrary values so that jsdom
 * can actually read them back — a `bg-[#8dacfb]` class is just a string to
 * the test, which is the kind of assertion that passes while the real thing
 * is wrong.
 *
 * (Laura asked for a fluffy red bow here. A bow at 6px is the same mistake as
 * the mark at 9px — the shape disappears and you are left with a red smudge.
 * The dot is the half of that idea that survives being small.)
 *
 * aria-hidden: the items are already discrete links to a screen reader, so
 * announcing a separator four times adds nothing. It is decoration.
 */
const FooterSeparator = () => (
  <span
    aria-hidden="true"
    className="h-1 w-1 shrink-0 select-none rounded-full"
    style={{
      backgroundColor: BOX_BLUE,
      boxShadow: `0 0 0 1px ${BOW_RED}`,
    }}
  />
);

/**
 * One footer entry — a link, carrying its own leading divider.
 *
 * The binding is the point. When the dividers were siblings of the links in
 * the wrapping row, a wrap could fall between a divider and the item it
 * introduced, stranding it at the end of a line with nothing after it — a
 * mark separating a line break that had already done the separating.
 * Measured at 520px and 430px, so phone width.
 *
 * Pairing each divider with its own label makes the pair one flex item, so a
 * wrap can only fall BETWEEN entries and a divider is always followed by the
 * thing it introduces.
 *
 * A line may now BEGIN with a divider. That is the trade, and it is the right
 * way round: a mark leading its own label reads as a bullet, whereas one
 * trailing nothing reads as a typo. The alternative — binding each divider to
 * the item before it — just moves the strand back to the end of the line.
 *
 * gap-2 matches the row's gap-x-2, so the divider sits equidistant from the
 * label before it and its own label after it.
 */
const FooterEntry = ({ children }: { children: ReactNode }) => (
  <span className="flex items-center gap-2">
    <FooterSeparator />
    {children}
  </span>
);

const Footer = () => {
  const pathname = usePathname();
  const showFooter =
    ROUTE_PREFIXES_TO_SHOW_FOOTER.some((item) => {
      return pathname?.startsWith(item);
    }) || pathname === "/";

  if (!showFooter) return null;

  return (
    <footer className="flex w-full flex-row flex-wrap items-center justify-center gap-x-2 gap-y-1 p-2">
      {/*
        The CLOSED box is the corporate mark of WrapDotGifts, Inc. The OPEN box
        is the Wrap.Gifts product mark and lives in the navbar. #767 removed the
        closed box from product surfaces because it was standing in for the
        product; this is its corrected use — beside the corporate entity, where
        it identifies the company rather than the goods.

        Only ONE mark belongs in this line. Putting both here would undo that
        separation, and the open box is already on every page in the navbar.

        The "is a service of" wording is load-bearing, not decoration: a mark
        beside a bare copyright notice reads as TRADE NAME use, which by itself
        establishes no trademark rights. This sentence is what associates the
        corporate entity with the services actually offered.
      */}
      <span className="flex items-center gap-1.5 text-xs text-gray-500">
        {/*
          Three separate things are pinned on this element. All three were
          bugs, and none of them is visible in a warm render.

          1. SIZE. The source artwork's viewBox was 600x600 around ink
             spanning only 331x364, offset low — 151 units of headroom against
             84 below. At h-4 the VISIBLE mark was ~9x10px inside a 16px box,
             and it sat below the text's centre no matter what the parent's
             items-center did, because the padding was baked into the file
             rather than the CSS box. The viewBox is now cropped square around
             the ink, so the box IS the mark. Square because OrganizationSchema
             serves this same file as parentOrganization.logo, where a crawler
             renders it at its own aspect ratio. Full opacity too: the closed
             box's palette is already pale (#b5c9ff lid) and 80% on top of that
             is what made it read as a washed-out tile.

          2. width/height are NOT redundant with h-5 w-5. SVGR emits a bare
             inline <svg> with only a viewBox, and an inline SVG with a viewBox
             but no intrinsic size resolves `width: auto` against its
             CONTAINER — measured at 1377x1377, making the footer 1399px tall.
             That is what the page paints between the SSR'd HTML arriving and
             the stylesheet applying, so a COLD RELOAD flashed an enormous mark
             and a collapsed layout while a warm navigation looked correct.
             The attributes govern that window; the classes govern afterwards.
             Both say 20, so nothing moves when CSS lands. Keep them in sync.

          3. The -0.125em lift is an OPTICAL correction, not a centring fix —
             items-center already centres the boxes exactly. The mark is taller
             than the type beside it, so box-centred it cleared the cap height
             by only 0.97px while hanging 5.08px past the baseline, which reads
             as sagging. 0.125em (1.5px at this 12px text) sits between the two
             defensible targets: balancing the mark's alpha-weighted visual
             mass on the cap band wants 1.32px, balancing the solid box on it
             wants 2.06px. In em so it survives a font-size change.

             This correction lives here rather than in the viewBox on purpose.
             The artwork stays a clean crop, which is what the standalone file
             needs as a logo; aligning it to THIS text is the footer's job.

          4. aria-hidden, NOT role="img" + aria-label="WrapDotGifts, Inc.".
             The sentence immediately after this element already says
             "WrapDotGifts, Inc.", so labelling the mark made a screen reader
             announce the company name twice in a row. The mark is not a link
             and carries nothing the sentence does not — decorative, by the
             definition that matters.

             This does NOT weaken the trademark purpose of putting the mark
             here. Trademark use is visual display in commerce; the
             accessibility tree is not part of it, and the mark still renders.
             Noted because this PR is trademark-driven and the obvious
             instinct on seeing an unlabelled logo is to "fix" it.
        */}
        <CorporateMark
          aria-hidden="true"
          width={20}
          height={20}
          className="h-5 w-5 shrink-0 -translate-y-[0.125em] select-none"
        />
        Wrap.Gifts is a service of WrapDotGifts, Inc. © {COPYRIGHT_YEAR}
      </span>
      <FooterEntry>
        <Link
          href="https://about.wrapdotgifts.com"
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          About Us
        </Link>
      </FooterEntry>
      <FooterEntry>
        <Link
          href="/policies"
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Policies
        </Link>
      </FooterEntry>
      {/* Links to the on-page FAQ at the home page's #faq anchor. Named
          "eCard FAQ" for AI-engine discovery: "eCard" binds the entity
          to our product and "FAQ" aligns with the FAQPage JSON-LD at the
          target, so crawlers can follow footer label → schema-typed Q&A.
          On the home page a plain <a> guarantees native same-page hash
          scrolling; on other footer routes (e.g. /myAccount, /fathersday)
          a <Link> does a client-side nav to `/` and Next scrolls to the
          hash — avoiding a full page reload. */}
      <FooterEntry>
        {pathname === "/" ? (
          <a href="/#faq" className="text-xs text-gray-500 hover:text-gray-700">
            eCard FAQ
          </a>
        ) : (
          <Link
            href="/#faq"
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            eCard FAQ
          </Link>
        )}
      </FooterEntry>
      <FooterEntry>
        <a
          href="#"
          className="termly-display-preferences text-xs text-gray-500 hover:text-gray-700"
          onClick={(e) => {
            e.preventDefault();
            if (typeof window.displayPreferenceModal === "function") {
              window.displayPreferenceModal();
            }
          }}
        >
          Consent Preferences
        </a>
      </FooterEntry>
    </footer>
  );
};

export default Footer;
